import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSeo } from "@/hooks/usePageSeo";

const TermsOfServicePage = () => {
  const { language, url, onLanguageChange } = usePageSeo();

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Terms of Service - ROC Worldwide Agency LLC</title>
        <meta name="description" content="Terms of Service for ROC Worldwide Agency LLC and SEO consulting services. Read our terms and conditions." />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Service - Luis ROC" />
        <meta property="og:description" content="Terms of Service for ROC Worldwide Agency LLC and SEO consulting services." />
        <meta property="og:url" content={url} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="section-padding">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="text-center">
                <h1 className="text-3xl lg:text-4xl font-bold mb-4 heading-gradient">
                  Terms of Service
                </h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              <Card className="card-elegant">
                <CardContent className="p-8 space-y-6">
                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">1. Agreement to Terms</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      By accessing and using the services provided by ROC Worldwide Agency LLC ("Company," "we," "us," or "our"), 
                      operating from the Houston, TX metro area, you accept and agree to be bound by the terms and provision 
                      of this agreement. If you do not agree to abide by the above, please do not use this service.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">2. Description of Service</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Luis ROC provides digital marketing consulting services, including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Search Engine Optimization (SEO) consulting and implementation</li>
                      <li>Search Engine Marketing (SEM) and Google Ads management</li>
                      <li>Amazon marketplace optimization and consulting</li>
                      <li>Digital marketing strategy development</li>
                      <li>Website audit and optimization recommendations</li>
                      <li>Brand development and rebranding services</li>
                      <li>Content strategy and marketing consultation</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">3. Client Responsibilities</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      As a client, you agree to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Provide accurate and complete information necessary for service delivery</li>
                      <li>Respond to communications in a timely manner</li>
                      <li>Provide access to necessary accounts, websites, and platforms as required</li>
                      <li>Make timely payments as agreed upon in the service agreement</li>
                      <li>Comply with all applicable laws and regulations in your business operations</li>
                      <li>Not engage in any illegal, unethical, or fraudulent business practices</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">4. Payment Terms</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Payment terms will be specified in individual service agreements. General payment terms include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Monthly retainer fees are due on the first day of each service month</li>
                      <li>Project-based fees are typically 50% upfront, 50% upon completion</li>
                      <li>Late payments may incur a 1.5% monthly service charge</li>
                      <li>Services may be suspended for accounts more than 30 days past due</li>
                      <li>All fees are non-refundable unless otherwise specified in writing</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">5. Intellectual Property</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      All strategies, methodologies, tools, and proprietary processes developed by Luis ROC remain the intellectual property 
                      of the Company. Clients retain ownership of their business information, content, and materials provided to us. 
                      Any custom work created specifically for the client (such as branded content or custom strategies) becomes the 
                      property of the client upon full payment.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">6. Confidentiality</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We maintain strict confidentiality regarding all client information, business data, strategies, and proprietary 
                      information shared during the course of our professional relationship. We will not disclose any confidential 
                      information to third parties without explicit written consent, except as required by law.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">7. Results and Performance</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      While we employ industry best practices and proven strategies, digital marketing results can vary based on 
                      numerous factors including market conditions, competition, algorithm changes, and client implementation. 
                      We do not guarantee specific results, rankings, or revenue increases. Our commitment is to provide professional, 
                      strategic guidance based on current industry standards and best practices.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">8. Limitation of Liability</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      In no event shall ROC Worldwide Agency LLC be liable for any indirect, incidental, special, 
                      consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other 
                      intangible losses, resulting from your use of our services. Our total liability shall not exceed the amount paid 
                      by the client for services in the twelve (12) months preceding the claim.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">9. Termination</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Either party may terminate the service agreement with thirty (30) days written notice. Upon termination, 
                      all outstanding invoices become immediately due and payable. We will provide a final report of work completed 
                      and transfer any applicable assets or accesses back to the client within a reasonable timeframe.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">10. Governing Law</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      These terms shall be governed by and construed in accordance with the laws of the State of Texas, 
                      United States of America. Any disputes arising under these terms shall be subject to the exclusive 
                      jurisdiction of the courts located in Montgomery County, Texas.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">11. Changes to Terms</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting 
                      to our website. Continued use of our services after any such changes constitutes your consent to such changes.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">12. Contact Information</h2>
                    <div className="text-muted-foreground leading-relaxed">
                      <p className="mb-2">
                        <strong>ROC Worldwide Agency LLC</strong>
                      </p>
                      <p>9002 Six Pines Dr, Suite 277</p>
                      <p>Shenandoah, TX 77380, United States</p>
                      <p>Phone: (832) 521-5050</p>
                      <p>Email: luisroc@luisroc.com</p>
                    </div>
                  </section>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default TermsOfServicePage;